import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LogOut, Menu, ShieldCheck } from "lucide-react";
import SidebarNav, { tutteLeVoci } from "./SidebarNav";
import UserMenu from "./UserMenu";
import ShowroomSelector from "./ShowroomSelector";
import { NuovoFoglio, NuovoMenu, vociNuovo } from "./Nuovo";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useRuoloUtente } from "@/hooks/use-ruolo-utente";
import { useMedia } from "@/hooks/use-media";
import { PERSONALI, arrivoDi, rapideDi } from "@/lib/accessi";
import { cn } from "@/lib/utils";
import { ReactComponent as LogoIdA } from "./logo-ida.svg";
import strakeBianco from "./strake-bianco.svg";

/* Il guscio del sistema del 29-10.
   · Computer, da 1024: menu blu fisso da 256 px (logo, «Nuovo», sede, gruppi, persona, Powered by Strake).
   · Fra 768 e 1024: il menu si chiude in una testata blu da 56 px col pulsante del menu e «Nuovo»
     (la regola del pacchetto, non disegnata).
   · Telefono, sotto 768: testata blu da 56 px e barra in basso con tre voci del ruolo, «Menu» e «Nuovo».
   Si monta un guscio solo per volta: così i testid (user-menu-trigger, nav-…) esistono una volta sola. */

function Logo({ className }) {
  return (
    <Link to="/" className="flex h-11 items-center rounded-sm px-2.5">
      <LogoIdA className={cn("w-auto shrink-0 text-white", className)} data-testid="logo-ida" />
    </Link>
  );
}

function PoweredBy() {
  return (
    <p className="flex items-center gap-2 px-3 text-caption font-normal text-white/75" data-testid="powered-by">
      Powered by
      <img src={strakeBianco} alt="Strake" className="h-3.5 w-auto" />
    </p>
  );
}

/* La persona nel menu del telefono: senza menu a tendina, con «Esci» accanto (tavola U-menu-390) e sotto
   «Il tuo accesso» (profilo e codice di verifica), che è di ogni persona (lib/accessi.js): così sta «nel menu col tuo nome» anche qui. */
function PersonaFoglio() {
  const { profile, session, signOut } = useAuth() || {};
  const { stato, ruolo } = useRuoloUtente();
  const email = profile?.email || session?.user?.email || null;
  const nome = profile?.nome ? `${profile.nome} ${profile.cognome || ""}`.trim() : email || "Utente";
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-nav-button font-bold text-nav-button-ink" aria-hidden="true">
        {(profile?.nome?.[0] || email?.[0] || "?").toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{nome}</span>
        <span className="block truncate text-caption font-normal text-white/75">
          {stato === "noto" ? ruolo.label : stato === "attesa" ? "" : "Ruolo non disponibile"}
        </span>
      </span>
      <button
        type="button"
        onClick={signOut}
        data-testid="menu-esci"
        className="inline-flex h-11 items-center gap-2 rounded-sm px-3 font-semibold hover:bg-white/10"
      >
        <LogOut className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
        Esci
      </button>
    </div>
  );
}

function SicurezzaFoglio({ onNavigate }) {
  const { profile } = useAuth() || {};
  if (!arrivoDi(profile?.ruolo)) return null;
  return (
    <Link
      to={PERSONALI.sicurezza}
      onClick={onNavigate}
      data-testid="menu-sicurezza"
      className="flex h-11 items-center gap-3 rounded-sm px-3 font-medium text-white/85 hover:bg-white/10 hover:text-white"
    >
      <ShieldCheck className="size-5" strokeWidth={1.75} aria-hidden="true" />
      Il tuo accesso
    </Link>
  );
}

function BarraTelefono({ onMenu }) {
  const { profile } = useAuth() || {};
  const voci = rapideDi(profile?.ruolo, profile?.sezioni_spente)
    .map((id) => tutteLeVoci.find((v) => v.id === id))
    .filter(Boolean);
  const conNuovo = vociNuovo(profile?.ruolo, profile?.sezioni_spente).length > 0;
  const voce = "flex min-h-[52px] min-w-0 flex-col items-center justify-center gap-0.5 text-caption transition-colors duration-150 ease-enter";
  return (
    <nav
      aria-label="Navigazione rapida"
      className="fixed inset-x-0 bottom-0 z-30 grid items-center gap-0.5 border-t border-line bg-surface pb-[calc(6px+env(safe-area-inset-bottom))] pl-1 pr-2.5 pt-1.5"
      style={{ gridTemplateColumns: `repeat(${voci.length + 1}, minmax(0, 1fr))${conNuovo ? " minmax(0, 1.6fr)" : ""}` }}
    >
      {voci.map((v) => {
        const Icon = v.icon;
        return (
          <NavLink
            key={v.id}
            to={v.path}
            end={v.path === "/preventivi"}
            data-testid={`bottom-nav-${v.id}`}
            className={({ isActive }) => cn(voce, isActive ? "font-bold text-brand-ink" : "font-medium text-ink-2")}
          >
            {({ isActive }) => (
              <>
                <span className={cn("flex h-7 w-11 items-center justify-center rounded-sm", isActive && "bg-brand-subtle")}>
                  <Icon className="size-[22px]" strokeWidth={1.75} aria-hidden="true" />
                </span>
                <span className="max-w-full truncate">{v.breve || v.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
      <button type="button" data-testid="bottom-nav-more" onClick={onMenu} className={cn(voce, "font-medium text-ink-2")}>
        <span className="flex h-7 w-11 items-center justify-center">
          <Menu className="size-[22px]" strokeWidth={1.75} aria-hidden="true" />
        </span>
        Menu
      </button>
      {conNuovo ? <NuovoFoglio /> : null}
    </nav>
  );
}

export default function AppLayout() {
  const [menuAperto, setMenuAperto] = useState(false);
  const computer = useMedia("(min-width: 1024px)");
  const telefono = !useMedia("(min-width: 768px)");

  return (
    <div className="min-h-screen bg-page">
      {computer ? (
        <aside className="on-blue fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-nav px-3.5 pb-4 pt-[calc(20px+env(safe-area-inset-top))] text-white">
          <Logo className="h-5" />
          <div className="mt-3 flex flex-col gap-2.5">
            <NuovoMenu suMenu />
            <ShowroomSelector suBlu />
          </div>
          {/* Le voci scorrono da sole se lo schermo è basso: Nuovo, sede e persona restano dove sono. */}
          <div className="-mx-1 mt-4 min-h-0 flex-1 overflow-y-auto px-1 pb-2">
            <SidebarNav />
          </div>
          <div className="flex flex-col gap-3.5 border-t border-white/15 pt-3">
            <UserMenu />
            <PoweredBy />
          </div>
        </aside>
      ) : (
        <header className="on-blue sticky top-0 z-30 bg-nav pt-[env(safe-area-inset-top)] text-white">
          <div className="flex h-14 items-center gap-0.5 pl-1 pr-1.5 md:gap-2 md:px-4">
            <button
              type="button"
              data-testid="mobile-menu-trigger"
              onClick={() => setMenuAperto(true)}
              className="inline-flex size-11 items-center justify-center rounded-md hover:bg-white/10"
              aria-label="Apri il menu"
            >
              <Menu className="size-[22px]" strokeWidth={1.75} aria-hidden="true" />
            </button>
            <Logo className="h-4" />
            <div className="flex-1" />
            {telefono ? null : <NuovoMenu />}
            <UserMenu compatto />
          </div>
          <Sheet open={menuAperto} onOpenChange={setMenuAperto}>
            <SheetContent
              side="left"
              className="on-blue flex w-80 max-w-[86vw] flex-col gap-0 border-0 bg-nav p-0 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-white [&>button:hover]:bg-white/10 [&>button:hover]:text-white [&>button]:text-white"
            >
              <SheetTitle className="sr-only">Menu principale</SheetTitle>
              <SheetDescription className="sr-only">Le sezioni del gestionale. Scegline una per aprirla; il menu si chiude da solo.</SheetDescription>
              <div className="flex h-14 items-center px-2.5">
                <Logo className="h-4" />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-2">
                <SidebarNav onNavigate={() => setMenuAperto(false)} />
              </div>
              <div className="flex flex-col gap-1 border-t border-white/15 px-3 pb-4 pt-3">
                <PersonaFoglio />
                <SicurezzaFoglio onNavigate={() => setMenuAperto(false)} />
                <PoweredBy />
              </div>
            </SheetContent>
          </Sheet>
        </header>
      )}

      <main
        className={cn(
          "px-4 pt-4 md:px-6 md:pb-10 md:pt-6 lg:ml-64 lg:px-9 lg:pb-12 lg:pt-[calc(28px+env(safe-area-inset-top))]",
          telefono && "pb-[calc(84px+env(safe-area-inset-bottom))]",
        )}
      >
        <div className="max-w-[1200px]">
          <Outlet />
        </div>
      </main>

      {telefono ? <BarraTelefono onMenu={() => setMenuAperto(true)} /> : null}
    </div>
  );
}
